import {type UIChild, UI} from "../../stem-core/ui/UIBase";
import {EndpointPaginator} from "../../stem-core/state/pagination/EndpointPaginator";
import {Dispatchable} from "../../stem-core/base/Dispatcher";
import type {ColumnInput} from "../../stem-core/base/ColumnHandler";
import {AggregateDataGrouping} from "../../client/state/merchant/AggregateDataGroupingStore";
import {isDeepEqual} from "../../blinkpay/UtilsLib";
import {ChevronDownIcon, ChevronUpIcon} from "../../core/ui/SVGElements.jsx";
import {CheckboxInput} from "../../stem-core/ui/input/checkbox/CheckboxInput.jsx";
import {camelCaseToCapitalizedWords} from "../misc/Formatting";

export function makeMetadataColumnName(key: string) {
    // TODO: Special case for panelId, panelAlias, journeyId and journeyAlias.
    return camelCaseToCapitalizedWords(key);
}

export function makeMetadataColumnFunction(key: string): ColumnValueFunction {
    // TODO: Special case for panelId, panelAlias, journeyId and journeyAlias.
    return value => value ?? "";
}

type ColumnValueFunction = (value: any, entry?: Record<string, any>) => UIChild;

export class GroupByField {
    // All assigned in the constructor
    declare name: string;
    declare field: string;
    declare columnFunction: ColumnValueFunction;
    declare columnOptions?: Record<string, any>;

    constructor(name: string, field: string, columnFunction?: ColumnValueFunction, columnOptions?: Record<string, any>) {
        this.name = name;
        this.field = field;
        this.columnFunction = columnFunction || (value => value);
        this.columnOptions = columnOptions;
    }

    toTableColumn(): ColumnInput<AggregateDataGrouping> {
        let previousGroupByFields: symbol | string[] = Symbol();
        let previousValue = Symbol();
        return [this.name, (obj: AggregateDataGrouping) => {
            const value = obj.entry[this.field];
            if (obj.groupByFields.indexOf(this.field) === -1) {
                previousValue = Symbol();
                previousGroupByFields = Symbol();
                return "(Any)";
            }
            if (value === previousValue && !isDeepEqual(obj.groupByFields, previousGroupByFields)) {
                return "";
            }
            previousValue = value;
            previousGroupByFields = obj.groupByFields;
            if (value == null) {
                return "N/A";
            }
            return this.columnFunction(value, obj.entry) ?? "N/A";
        }, this.columnOptions];
    }
}

export class BaseAnnotation {
    // All assigned in the constructor
    declare name: string;
    declare field: string | null;
    declare columnFunction: ColumnValueFunction;
    declare columnOptions?: Record<string, any>;

    constructor(name: string, field: string | null, columnFunction?: ColumnValueFunction, columnOptions?: Record<string, any>) {
        this.name = name;
        this.field = field;
        this.columnFunction = columnFunction || (value => value);
        this.columnOptions = columnOptions;
    }

    toTableColumn(): ColumnInput<AggregateDataGrouping> {
        return [this.name, (obj: AggregateDataGrouping) => {
            const value = obj.entry[this.field];
            if (value == null) {
                return "N/A";
            }
            return this.columnFunction(value, obj.entry) ?? "N/A"
        }, this.columnOptions];
    }
}

export class Annotation extends BaseAnnotation {
    declare func: string;
    declare target: string;

    constructor(name: string, field: string, func: string, target: string, columnFunction?: ColumnValueFunction, columnOptions?: Record<string, any>) {
        super(name, field, columnFunction, columnOptions);
        this.func = func;
        this.target = target;
    }
}

export class AnalyticsAnnotation extends BaseAnnotation {
    declare eventTypes: string;
    declare payload: Record<string, any>;

    constructor(name: string, field: string, eventTypes: string, payload: Record<string, any>, columnFunction?: ColumnValueFunction, columnOptions?: Record<string, any>) {
        super(name, field, columnFunction, columnOptions);
        this.eventTypes = eventTypes;
        this.payload = payload;
    }
}

export class ExtraColumnAnnotation extends BaseAnnotation {
    constructor(name: string, columnFunction: ColumnValueFunction, columnOptions?: Record<string, any>) {
        super(name, null, columnFunction, columnOptions);
    }

    toTableColumn(): ColumnInput<AggregateDataGrouping> {
        return [this.name, (obj: AggregateDataGrouping) => this.columnFunction(obj.entry) ?? "N/A", this.columnOptions];
    }
}

export class AggregateDataViewManager extends Dispatchable {
    // All assigned in the constructor, apart from the paginator which is built on demand
    declare builtinAnnotations: BaseAnnotation[];
    declare availableAnnotations: BaseAnnotation[];
    declare builtinGroupByFields: GroupByField[];
    declare availableGroupByFields: GroupByField[];
    declare enabledGroupByFields: GroupByField[];
    declare enabledAnnotations: BaseAnnotation[];
    declare paginator?: AggregateDataPaginator;

    constructor(builtinAnnotations: BaseAnnotation[], builtinGroupByFields: GroupByField[]) {
        super();

        this.builtinAnnotations = [
            new Annotation("Count", "count", "count", "*"),
            ...builtinAnnotations,
        ];
        // TODO Metadata annotations? Annotation builder? whatever
        this.availableAnnotations = [...this.builtinAnnotations];

        this.builtinGroupByFields = builtinGroupByFields;
        this.availableGroupByFields = [...this.builtinGroupByFields];

        this.enabledGroupByFields = [];
        this.enabledAnnotations = [...this.builtinAnnotations];
    }

    getPaginator(endpoint: string, options: Record<string, any>) {
        if (!this.paginator) {
            this.paginator = new AggregateDataPaginator(this, endpoint, options);
            this.attachListener(this.paginator, "pageLoaded", () => this.dispatchChange());
        }
        return this.paginator;
    }

    getLastResponse() {
        return this.paginator?.getLastResponse();
    }

    addMetadataKeys(metadataKeys: string[]) {
        for (const metadataKey of metadataKeys) {
            this.availableGroupByFields.push(new GroupByField(makeMetadataColumnName(metadataKey), `conversion__metadata__${metadataKey}`));
        }
    }

    toggleGroupByField(groupByField: GroupByField) {
        const currentIndex = this.enabledGroupByFields.indexOf(groupByField);
        if (currentIndex >= 0) {
            this.enabledGroupByFields.splice(currentIndex, 1);
        } else {
            this.enabledGroupByFields = this.availableGroupByFields.filter(
                availableField => this.enabledGroupByFields.indexOf(availableField) >= 0 || availableField === groupByField);
        }
        this.dispatchChange();
    }

    swapGroupByFieldIndex(index: number) {
        if (index < 0 || index >= this.availableGroupByFields.length - 1) {
            return;
        }
        const temp = this.availableGroupByFields[index];
        this.availableGroupByFields[index] = this.availableGroupByFields[index + 1];
        this.availableGroupByFields[index + 1] = temp;
        // Remake the order.
        this.enabledGroupByFields = this.availableGroupByFields.filter(field => this.enabledGroupByFields.indexOf(field) >= 0);
        this.dispatchChange();
    }

    getRequestGroupsField() {
        const annotations: Record<string, {function: string; target: string}> = {};
        const analyticsAnnotations: Record<string, {event_type: string; payload: Record<string, any>}> = {};
        for (const annotation of this.enabledAnnotations) {
            if (annotation instanceof Annotation) {
                annotations[annotation.field] = {
                    function: annotation.func,
                    target: annotation.target,
                };
            } else if (annotation instanceof AnalyticsAnnotation) {
                analyticsAnnotations[annotation.field] = {
                    event_type: annotation.eventTypes,
                    payload: annotation.payload,
                };
            }
        }
        return {
            groupBy: this.enabledGroupByFields.map(field => field.field),
            annotations,
            analyticsAnnotations,
        };
    }

    getTableColumns() {
        return [
            ...this.enabledGroupByFields.map(field => field.toTableColumn()),
            ...this.enabledAnnotations.map(annotation => annotation.toTableColumn()),
        ];
    }
}

function generateAggregateOrderedEntries(entries: AggregateDataGrouping[], fields: GroupByField[], options?: any): AggregateDataGrouping[];
function generateAggregateOrderedEntries(entries: AggregateDataGrouping[], fields: GroupByField[]): AggregateDataGrouping[] {
    if (fields.length === 0 || entries.length <= 1) {
        return entries;
    }
    const [groupByField, ...remainingFields] = fields;
    const entriesByFieldValue = new Map<unknown, AggregateDataGrouping[]>();
    for (const entry of entries) {
        const fieldValue = entry.entry[groupByField.field];
        if (!entriesByFieldValue.has(fieldValue)) {
            entriesByFieldValue.set(fieldValue, []);
        }
        entriesByFieldValue.get(fieldValue).push(entry);
    }
    const groups = [...entriesByFieldValue.entries()].sort(([,entries1], [,entries2]) => {
        const group1Count = entries1.reduce((count, entry) => count + entry.entry.count, 0);
        const group2Count = entries2.reduce((count, entry) => count + entry.entry.count, 0);
        return group2Count - group1Count;
    });
    const newEntries = [];
    for (const [, groupEntries] of groups) {
        if (groupEntries.length === 1) {
            newEntries.push(...groupEntries);
        } else {
            newEntries.push(
                ...generateAggregateOrderedEntries(groupEntries, remainingFields),
            );
        }
    }
    return newEntries;
}

class AggregateDataPaginator extends EndpointPaginator<AggregateDataGrouping> {
    disablePaginationController = true;

    declare groupsManager: AggregateDataViewManager;

    constructor(groupsManager: AggregateDataViewManager, endpoint: string, options: Record<string, any>) {
        super(AggregateDataGrouping, endpoint, options);
        this.groupsManager = groupsManager;
        this.filters.groups = this.groupsManager.getRequestGroupsField();
        this.attachChangeListener(this.groupsManager, () => {
            this.updateFilter({
                groups: this.groupsManager.getRequestGroupsField(),
            });
        });
    }

    getNumPages() {
        return 1;
    }

    getCurrentPageEntries() {
        const rawEntries = super.getCurrentPageEntries();
        if (!rawEntries || !rawEntries.length || !this.groupsManager.enabledGroupByFields.length) {
            return rawEntries;
        }
        const entries = generateAggregateOrderedEntries(rawEntries, this.groupsManager.enabledGroupByFields, {});
        // Filter out aggregate entries that only have a single group below them, as they only clutter the UI.
        return entries.filter((entry, index) => {
            return index + 1 === entries.length
                || entry.entry.count !== entries[index + 1].entry.count
                || entry.groupByFields.length >= entries[index + 1].groupByFields.length;
        });
    }
}

interface AggregateDataFiltersOptions {
    groupsManager?: AggregateDataViewManager;
}

export class AggregateDataFilters extends UI.Element<AggregateDataFiltersOptions> {
    render() {
        const {groupsManager} = this.options;
        return [
            "Group by",
            groupsManager.availableGroupByFields.map((groupByField, index) => <div key={`groupBy##${groupByField.field}`} style={{display: "flex"}}>
                <ChevronUpIcon size={21} style={{cursor: index > 0 ? "pointer" : "not-allowed", display: "inline-block"}}
                               onClick={() => groupsManager.swapGroupByFieldIndex(index - 1)}/>
                <ChevronDownIcon size={21} style={{cursor: index < groupsManager.availableGroupByFields.length - 1 ? "pointer" : "not-allowed", display: "inline-block"}}
                                 onClick={() => groupsManager.swapGroupByFieldIndex(index)}/>
                <CheckboxInput label={groupByField.name}
                               initialValue={groupsManager.enabledGroupByFields.indexOf(groupByField) >= 0}
                               onChange={() => groupsManager.toggleGroupByField(groupByField)}/>
            </div>),
        ]
    }
}
